import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { ToastService } from '@core/services/toast.service';
import { extractErrorMessage, isSoftErrorResponse } from '@core/utils/extract-error-message.util';
import * as Sentry from '@sentry/angular';

/**
 * Symbol stamped onto every HttpErrorResponse that ErrorHandlerInterceptor
 * has already shown a toast for. Components can check this to avoid a
 * duplicate toast. Use isInterceptorHandled() from @core/utils/api-error.util.
 */
export const INTERCEPTOR_HANDLED = Symbol('interceptorHandled');

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private toastService = inject(ToastService);

  /**
   * URLs that should be excluded from global error handling
   * Add URLs here if they handle errors in their own way
   */
  private excludedUrls: string[] = [
    // Add any URLs that should handle their own errors
    // Example: '/api/custom-error-handling'
    "api/note",
    "api/shipping/getStores",
    "api/Search/Advance",
    // Orders Review: backend returns Status:false ("No orders under review are found")
    // for a perfectly normal zero-result filter/tab. The table already shows its own
    // "No Records Found" empty state, so the interceptor's auto-toast is pure noise here
    // — most visibly on page load/refresh when background calls (e.g. IEH pending count)
    // silently return 0 rows. Components still show their own toast for genuine failures.
    "api/orders/review-details"
  ];

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap((event: HttpEvent<any>) => {
        // Soft-error detection: 200 OK responses where Status/success === false.
        // The component reads only response.message — the interceptor reads both
        // message + error (string) via extractErrorMessage and shows the full
        // combined toast. suppressErrorsFor(300) then silently drops the
        // component's own response.message toast (zero-component-change).
        const skipErrorToast = req.headers.get('X-Skip-Error-Toast');
        if (event instanceof HttpResponse && !this.shouldExcludeUrl(req.url) && skipErrorToast !== 'true') {
          const body = event.body;
          if (isSoftErrorResponse(body)) {
            const message = extractErrorMessage(body);
            const endpoint = this.formatEndpoint(req.url);
            const title = endpoint ? `Error - ${endpoint}` : 'Error';
            this.toastService.error(title, message, 7000);
            this.toastService.suppressErrorsFor(300);
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // Check if this URL should be excluded from global error handling
        if (this.shouldExcludeUrl(req.url)) {
          return throwError(() => error);
        }

        // Check if this request has a retry header (to avoid showing toast on retry attempts)
        const retryCount = req.headers.get('X-Retry-Count');
        const skipErrorToast = req.headers.get('X-Skip-Error-Toast');

        // Only show toast if not a retry attempt and not explicitly skipped
        if (!retryCount && skipErrorToast !== 'true') {
          // Handle different types of errors
          if (error.error instanceof ErrorEvent) {
            // Client-side or network error
            this.handleClientError(error, req);
          } else {
            // Backend returned an unsuccessful response code
            this.handleServerError(error, req);
          }
        }

        // Stamp the error so components know the interceptor already showed a toast.
        // Check with isInterceptorHandled(err) from @core/utils/api-error.util.
        (error as any)[INTERCEPTOR_HANDLED] = true;

        // Re-throw the error so it can still be handled by the component if needed
        return throwError(() => error);
      })
    );
  }

  /**
   * Handle client-side errors (network errors, etc.)
   */
  private handleClientError(error: HttpErrorResponse, req: HttpRequest<any>): void {
    const errorMessage = extractErrorMessage(error, 'A network error occurred. Please check your connection.');

    // Report genuine network failures to Sentry (status 0 is filtered in beforeSend).
    // handled: false → network failures are not gracefully recoverable.
    Sentry.withScope(scope => {
      scope.setContext('http', { url: error.url, method: req.method, status: error.status });
      Sentry.captureException(error, { mechanism: { type: 'generic', handled: false } });
    });

    this.toastService.error(
      'Network Error',
      errorMessage,
      7000
    );
    // Open a 300ms suppress window so component catchError blocks don't double-toast.
    this.toastService.suppressErrorsFor(300);

    console.error('Client-side error:', error);
  }

  /**
   * Handle server-side errors (API errors)
   */
  private handleServerError(error: HttpErrorResponse, req: HttpRequest<any>): void {
    let title = 'Error';
    let message = 'An unexpected error occurred. Please try again.';

    // Get user-friendly endpoint name
    const endpoint = this.formatEndpoint(error.url || '');

    // Extract message from any API error format
    message = extractErrorMessage(error, message);

    // Set title based on HTTP status code
    switch (error.status) {
      case 400:
        title = 'Bad Request';
        break;
      case 401:
        // Skip showing toast for 401 - ApiUnauthorisedInterceptor handles logout
        // This prevents multiple toasts when multiple API calls fail simultaneously
        return;
      case 403:
        title = 'Forbidden';
        message = 'You do not have permission to perform this action.';
        break;
      case 404:
        title = 'Not Found';
        message = 'The requested resource was not found.';
        break;
      case 500:
        title = 'Server Error';
        break;
      case 502:
        title = 'Bad Gateway';
        message = 'The server is temporarily unavailable. Please try again later.';
        break;
      case 503:
        title = 'Service Unavailable';
        message = 'The service is temporarily unavailable. Please try again later.';
        break;
      case 504:
        title = 'Gateway Timeout';
        message = 'The request timed out. Please try again.';
        break;
      default:
        if (error.status === 0) {
          title = 'Connection Error';
          message = 'Unable to connect to the server. Please check your internet connection.';
        }
    }

    // Add endpoint to title if available
    if (endpoint) {
      title = `${title} - ${endpoint}`;
    }

    // Report actionable server errors (5xx + important 4xx) to Sentry before
    // showing the toast. 401 already returns early above; status 0 is filtered
    // in Sentry's beforeSend callback.
    const shouldCapture =
      error.status >= 500 ||
      error.status === 403 ||
      error.status === 404 ||
      error.status === 422;

    if (shouldCapture) {
      // 5xx = unhandled (server is broken, app cannot recover).
      // 4xx = handled (app shows toast and continues).
      const isUnhandled = error.status >= 500;
      Sentry.withScope(scope => {
        scope.setContext('http', {
          url: error.url,
          method: req.method,
          status: error.status,
          statusText: error.statusText,
        });
        Sentry.captureException(error, { mechanism: { type: 'generic', handled: !isUnhandled } });
      });
    }

    // Show error toast
    this.toastService.error(title, message, 7000);
    // Open a 300ms suppress window so component catchError blocks don't double-toast.
    this.toastService.suppressErrorsFor(300);

    // Log error for debugging
    console.error('Server error:', {
      status: error.status,
      statusText: error.statusText,
      url: error.url,
      endpoint: endpoint,
      error: error.error
    });
  }

  /**
   * Format API endpoint to user-friendly name
   * Example: "api/customer/something" -> "customer-something"
   */
  private formatEndpoint(url: string): string {
    if (!url) return '';

    try {
      // Extract pathname from full URL
      const urlObj = new URL(url, window.location.origin);
      let path = urlObj.pathname;

      // Remove leading slash
      path = path.replace(/^\/+/, '');

      // Remove 'api/' prefix if present
      path = path.replace(/^api\//i, '');

      // Split by '/' and join with '-'
      const parts = path.split('/').filter(part => part.length > 0);

      // Remove numeric IDs and query parameters
      const cleanedParts = parts.filter(part => !/^\d+$/.test(part));

      // Join with hyphens and convert to lowercase
      const formattedEndpoint = cleanedParts.join('-').toLowerCase();

      return formattedEndpoint || '';
    } catch (e) {
      // If URL parsing fails, try simple string manipulation
      return url
        .replace(/^.*\/api\//i, '') // Remove everything before and including /api/
        .replace(/\/\d+/g, '') // Remove numeric IDs
        .replace(/\?.*$/, '') // Remove query parameters
        .replace(/\//g, '-') // Replace slashes with hyphens
        .toLowerCase();
    }
  }

  /**
   * Check if URL should be excluded from global error handling
   */
  private shouldExcludeUrl(url: string): boolean {
    return this.excludedUrls.some(excludedUrl =>
      url.toLowerCase().includes(excludedUrl.toLowerCase())
    );
  }

  /**
   * Parse connection error messages for better user feedback
   */
  private parseConnectionError(errorMessage: string): string {
    if (errorMessage.includes('Connection refused')) {
      return 'Unable to connect to the server. The service may be temporarily unavailable.';
    }
    if (errorMessage.includes('tcp://127.0.0.1:6379')) {
      return 'Database connection error. Please contact support.';
    }
    return errorMessage;
  }
}
